import type { ChangeEvent } from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { VersioningActions } from "./types";
import { AddVersionButton } from "./VersionAction/AddVersionButton";
import { ChangeNameVersionAction } from "./VersionAction/ChangeNameVersionAction";
import { DeleteVersionAction } from "./VersionAction/DeleteVersionAction";
import { RestoreVersionAction } from "./VersionAction/RestoreVersionAction";
import { SaveVersionAction } from "./VersionAction/SaveVersionAction";
import { StorageObjectValue } from "../../services/api/instances/storageApi/types/service_types";
import { StorageObjectVersion } from "../../services/api/instances/storageApi/types/object_types";
import styles from "./styles.module.css";

interface Props<T extends StorageObjectValue> {
  versions: StorageObjectVersion<T>[];
  selectedVersion: StorageObjectVersion<T> | null;
  onOpenSaveVersionModal: (type: VersioningActions) => void;
  onOpenRestoreVersionModal: (type: VersioningActions) => void;
  onEditName: (name: string) => void;
  onRefreshVersion: () => void;
  onDeleteVersion: () => void;
  isSaveButtonDisabled: boolean;
}

export const VersioningBlock = <T extends StorageObjectValue>(
  props: Props<T>,
) => {
  const [versionName, setVersionName] = useState<string>(
    props.selectedVersion?.version_name!,
  );
  const { t } = useTranslation();

  useEffect(() => {
    setVersionName(props.selectedVersion?.version_name!);
  }, [props.selectedVersion?.version_name]);

  const handleNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setVersionName(event.target.value);
  };

  return (
    <div className={styles.container}>
      <div className={styles["version-label"]}>
        {t("versioning.active_version")}
      </div>
      <div className={styles["actions-wrapper"]}>
        <RestoreVersionAction
          name={props?.selectedVersion?.version_name as string}
          onClick={() => props.onOpenRestoreVersionModal("restore")}
        />
        <AddVersionButton
          onClick={() => props.onOpenSaveVersionModal("save")}
        />
        <SaveVersionAction
          name={props.selectedVersion?.version_name!}
          onRefreshVersion={props.onRefreshVersion}
          isDisabled={
            props.selectedVersion?.version_name === undefined ||
            props.isSaveButtonDisabled
          }
        />
        <ChangeNameVersionAction
          name={versionName}
          handleNameChange={handleNameChange}
          onEditName={() => props.onEditName(versionName)}
          isDisabled={props.versions.length === 0}
          onCancel={() =>
            setVersionName(props.selectedVersion?.version_name as string)
          }
        />
        <DeleteVersionAction
          name={props.selectedVersion?.version_name as string}
          onDelete={props.onDeleteVersion}
        />
      </div>
    </div>
  );
};
